import { Injectable } from '@angular/core';
import { NgForage } from 'ngforage';
import AssetHelper from 'src/app/helpers/asset-helper';
import ErrorHelper from 'src/app/helpers/error-helper';
import ProductAsset from 'src/app/model/product-asset';
import { RequestService } from '../request.service';
import { SnackService } from '../snack.service';
import { ImageCompressionService } from '../image-compression.service';
import { OfflineAsset } from './offline-asset';
import * as allSettled from 'promise.allsettled';

@Injectable({
  providedIn: 'root',
})
export class OfflineAssetSubmitService {
  private readonly compressIfLargerThan = 524288; // 0.5 MB

  constructor(
    private requestService: RequestService,
    private snackService: SnackService,
    private imageCompressionService: ImageCompressionService,
    private ngForage: NgForage
  ) {}

  public async submitOfflineAsset(
    offlineAsset: OfflineAsset,
    productId: number | null,
    subProjectId: number | null,
    immediateSubmit: boolean,
    showErrorMessage = false
  ): Promise<ProductAsset> {
    try {
      const file = await this.ngForage.getItem<File>(
        AssetHelper.getStorageKeyFor(offlineAsset.localFileUuid)
      );
      if (file == null) {
        throw new Error(`File no longer exists: ${offlineAsset.localFileUuid}`);
      }
      return new Promise(async (resolve, reject) => {
        const compressedFile =
          file.size > this.compressIfLargerThan
            ? await this.imageCompressionService.compressImage(file)
            : file;
        const base64 = await AssetHelper.extractBase64(compressedFile);
        const requestBody: OfflineAssetSubmitRequestBody = {
          name: compressedFile.name,
          type: compressedFile.type,
          size: compressedFile.size,
          formSection: offlineAsset.formSection,
          base64: base64,
          productId: productId,
          subProjectId: subProjectId,
          localFileUuid: offlineAsset.localFileUuid,
        };
        this.requestService
          .post(requestBody, '/productAssets/offlineAssetSubmit', null, false)
          .subscribe(
            (res: ProductAsset) => {
              resolve(res);
            },
            (err) => {
              if (
                ErrorHelper.isConnectionLostError(err) &&
                immediateSubmit &&
                showErrorMessage
              ) {
                this.snackService.showWarning(
                  'Server kon niet bereikt worden, project zal ingediend worden zodra internetverbinding hersteld is'
                );
              } else if (immediateSubmit && showErrorMessage) {
                this.snackService.showError(err);
              }
              reject(err);
            }
          );
      });
    } catch (err) {
      throw err;
    }
  }

  public async submitMultipleOfflineAssets(
    offlineAsset: OfflineAsset[],
    productId: number | null,
    subProjectId: number | null,
    immediateSubmit: boolean
  ): Promise<MultipleAssetSubmitResult> {
    const results = await allSettled(
      offlineAsset.map((a) =>
        this.submitOfflineAsset(
          a,
          productId,
          subProjectId,
          immediateSubmit,
          false
        )
      )
    );
    const resultMap = new Map<string, ProductAsset>();
    let error = null;
    for (const result of results) {
      if (result.status === 'fulfilled') {
        resultMap.set(result.value.localFileUuid, result.value);
      } else if (result.status === 'rejected') {
        error =
          error == null
            ? result.reason
            : ErrorHelper.isConnectionLostError(error)
            ? error
            : result.reason;
      }
    }
    return {
      resultMap: resultMap,
      error: error,
    };
  }
}

export class OfflineAssetSubmitRequestBody {
  name: string;
  type: string;
  size: number;
  formSection: string;
  base64: string;
  productId?: number;
  subProjectId?: number;
  localFileUuid: string;
}

export class MultipleAssetSubmitResult {
  resultMap: Map<string, ProductAsset>;
  error?: any;
}
